<template>
    <div class="subscription-banner">
        <div class="banner-content">
            <div class="banner-icon">
                <font-awesome-icon :icon="hasActiveSubscription ? 'check-circle' : 'info-circle'" />
            </div>
            <div class="banner-text-section">
                <div class="banner-title">
                    {{ $t('route_optimization.title') }}
                </div>
                <div class="banner-text">
                    <span v-if="isLoading || !subscription">{{ $t('common.loading') }}</span>
                    <span v-else>{{ hasActiveSubscription ? $t('subscription.active') : $t('subscription.trial_message') }}</span>
                </div>
            </div>
            <div
                v-if="!hasActiveSubscription && !isLoading"
                class="banner-actions"
            >
                <button
                    class="learn-more-btn"
                    @click="showBuyDialog = true"
                >
                    {{ $t('subscription.learn_more') }}
                </button>
            </div>
        </div>
    </div>

    <!-- Upgrade Dialog -->
    <div
        v-if="showBuyDialog"
        class="dialog-overlay"
        @click="showBuyDialog = false"
    >
        <div
            class="dialog"
            @click.stop
        >
            <div class="dialog-header">
                <h3>{{ $t('subscription.upgrade_title') }}</h3>
                <button
                    class="close-btn"
                    @click="showBuyDialog = false"
                >
                    &times;
                </button>
            </div>
            <div class="dialog-body">
                <p>{{ $t('subscription.upgrade_intro') }}</p>

                <div class="pricing-tiers">
                    <div class="tier-card">
                        <div class="tier-header">
                            <h4>{{ $t('subscription.tier_1') }}</h4>
                            <div class="tier-price">
                                99.9 € / {{ $t('subscription.per_month') }}
                            </div>
                        </div>
                        <div class="tier-description">
                            {{ $t('subscription.tier1_description') }}
                        </div>
                        <div class="tier-features">
                            <div class="tier-feature">
                                <font-awesome-icon
                                    icon="check"
                                    class="feature-icon"
                                />
                                {{ $t('subscription.optimize_routes_2000') }}
                            </div>
                        </div>
                    </div>

                    <div class="tier-card">
                        <div class="tier-header">
                            <h4>{{ $t('subscription.tier_2') }}</h4>
                            <div class="tier-price">
                                199.9 € / {{ $t('subscription.per_month') }}
                            </div>
                        </div>
                        <div class="tier-description">
                            {{ $t('subscription.tier2_description') }}
                        </div>
                        <div class="tier-features">
                            <div class="tier-feature">
                                <font-awesome-icon
                                    icon="check"
                                    class="feature-icon"
                                />
                                {{ $t('subscription.optimize_routes_10000') }}
                            </div>
                        </div>
                    </div>

                    <div class="tier-card tier-unlimited">
                        <div class="tier-header">
                            <h4>{{ $t('subscription.tier_unlimited') }}</h4>
                            <div class="tier-price">
                                {{ $t('subscription.contact_sales') }}
                            </div>
                        </div>
                        <div class="tier-description">
                            {{ $t('subscription.tier_unlimited_description') }}
                        </div>
                        <div class="tier-features">
                            <div class="tier-feature">
                                <font-awesome-icon
                                    icon="infinity"
                                    class="feature-icon"
                                />
                                {{ $t('subscription.unlimited_routes') }}
                            </div>
                        </div>
                    </div>
                </div>

                <div class="contact-section">
                    <p>{{ $t('subscription.upgrade_contact') }}</p>
                    <p class="email-link">
                        <a
                            :href="'mailto:' + $t('subscription.support_email')"
                            class="support-email"
                        >
                            {{ $t('subscription.support_email') }}
                        </a>
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'SubscriptionBanner',
    props: {
        subscription: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            showBuyDialog: false
        };
    },
    computed: {
        isLoading() {
            return this.$store.state.subscriptions.isLoading && !this.$store.state.subscriptions.subscription;
        },
        hasActiveSubscription() {
            return this.subscription?.route_optimization?.is_active || false;
        }
    }
};
</script>

<style scoped>
.subscription-banner {
    position: relative;
    top: 0;
    left: 0;
    right: 0;
    z-index: 10000;
    background: rgba(255, 255, 255, 0.95);
    color: #333;
    padding: 12px 16px;
    box-shadow: 0 2px 16px rgba(0, 0, 0, 0.12);
    backdrop-filter: blur(4px);
    border-radius: 8px;
    margin: 0.5rem 0;
}



.banner-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
}

.banner-icon {
    font-size: 20px;
    color: #7FC000;
    flex-shrink: 0;
}

.banner-text-section {
    flex: 1;
    min-width: 0;
}

.banner-title {
    font-size: 20px;
    font-weight: 600;
    margin-bottom: 4px;
}

.banner-text {
    font-size: 14px;
    font-weight: 400;
    opacity: 0.95;
}

.banner-actions {
    display: flex;
    gap: 8px;
    flex-shrink: 0;
}

.banner-usage {
    text-align: right;
    flex-shrink: 0;
}

.usage-title {
    font-size: 10px;
    color: #666;
    margin-bottom: 2px;
}

.usage-amount {
    font-size: 20px;
    font-weight: 600;
    color: #7FC000;
}

.learn-more-btn {
    background: transparent;
    color: #7FC000;
    border: 1px solid #7FC000;
    padding: 6px 12px;
    border-radius: 4px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
}

.learn-more-btn:hover {
    background: rgba(127, 194, 0, 0.1);
    border-color: #6BA800;
}

.upgrade-btn {
    background: #7FC000;
    color: white;
    border: none;
    padding: 6px 16px;
    border-radius: 4px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.upgrade-btn:hover {
    background: #6BA800;
    transform: translateY(-1px);
}

@media (max-width: 768px) {
    .banner-content {
        flex-direction: column;
        text-align: center;
        gap: 12px;
    }

    .banner-actions {
        justify-content: center;
    }
}

.dialog-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 10001;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0;
    padding: 1rem;
}

.dialog {
    background: white;
    border-radius: 8px;
    width: 90%;
    max-width: 700px;
    max-height: 90vh;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
    overflow: auto;
}

.dialog-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px;
    border-bottom: 1px solid #eee;
}

.dialog-header h3 {
    margin: 0;
    color: #003856;
    font-size: 1.5em;
}

.close-btn {
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: #666;
    padding: 0;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.close-btn:hover {
    color: #333;
}

.dialog-body {
    padding: 20px;
    color: #404041;
    line-height: 1.5;
}

.pricing-tiers {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-top: 20px;
}

.tier-card {
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    padding: 16px;
    transition: all 0.2s;
}

.tier-card:hover {
    border-color: #7FC000;
    box-shadow: 0 2px 8px rgba(127, 194, 0, 0.1);
}

.tier-unlimited {
    border-color: #7FC000;
    background: rgba(127, 194, 0, 0.05);
}

.tier-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
}

.tier-header h4 {
    margin: 0;
    color: #333;
    font-size: 18px;
}

.tier-price {
    font-weight: 600;
    color: #7FC000;
    font-size: 16px;
}

.tier-description {
    font-size: 13px;
    color: #666;
    margin-bottom: 12px;
    font-style: italic;
}

.tier-features {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.tier-feature {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
}

.feature-icon {
    color: #7FC000;
    font-size: 12px;
}

.contact-section {
    margin-top: 24px;
    padding-top: 20px;
    border-top: 1px solid #e0e0e0;
    text-align: center;
}

.email-link {
    margin-top: 12px;
}

.support-email {
    color: #7FC000;
    text-decoration: none;
    font-weight: 600;
}

.support-email:hover {
    color: #6BA800;
    text-decoration: underline;
}
</style>
